<!-- 
화면명 : ScmHome.vue
설명   : 메인 홈 화면 - 메뉴 바로가기 아이콘 리스트
-->
<template>
  <div class="h-full w-full p-3 xl:p-4 flex flex-col items-start justify-start select-none">
    <!-- 메뉴 아이콘 리스트 영역 (모바일: 3열 앱 런처 그리드, PC: 기존 컴팩트 플렉스 유지) -->
    <div class="w-full">
      <div
        v-if="displayMenus.length > 0"
        class="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:flex xl:flex-wrap items-start gap-2.5 sm:gap-3 xl:gap-4"
      >
        <button
          v-for="(menu, index) in displayMenus"
          :key="menu.path"
          type="button"
          @click="onMenuClick(menu)"
          class="group flex flex-col items-center justify-center w-full xl:w-24 py-3 xl:py-2 px-1 rounded-xl xl:rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-all duration-150 cursor-pointer focus:outline-none"
        >
          <!-- 48*48 사이즈 컬러풀 아이콘 -->
          <UCIcon
            :name="menu.icon"
            customClass="group-hover:scale-110 group-hover:filter group-hover:brightness-110 active:scale-95 transition-all duration-150"
            :style="{
              width: '48px',
              height: '48px',
              minWidth: '48px',
              minHeight: '48px',
              background: getMenuIconBg(menu, index)
            }"
          />

          <!-- 메뉴명 -->
          <span
            class="mt-2 text-xs font-medium text-gray-700 group-hover:text-gray-900 group-hover:font-semibold transition-colors duration-150 text-center break-keep leading-tight"
          >
            {{ menu.name }}
          </span>
        </button>
      </div>

      <!-- 로딩 중 상태 -->
      <div
        v-else-if="isLoading"
        class="flex flex-col items-center justify-center py-12 text-gray-400"
      >
        <i class="fas fa-spinner fa-spin text-2xl mb-2 text-blue-500"></i>
        <span class="text-xs">메뉴 정보를 불러오는 중입니다...</span>
      </div>

      <!-- 메뉴가 없을 때 -->
      <div
        v-else
        class="flex flex-col items-center justify-center py-12 text-gray-400"
      >
        <i class="fas fa-folder-open text-2xl mb-2 text-gray-300"></i>
        <span class="text-xs">표시할 메뉴가 없습니다.</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import { useRouter } from "vue-router";
import { useTabStore } from "@/store/tabStore";
import { useUserStore } from "@/store/userStore";
import UCIcon from "@/components/UserControl/UCIcon.vue";

const router = useRouter();
const tabStore = useTabStore();
const userStore = useUserStore();

const menus = ref([]);
const isLoading = ref(false);

// 컬러 정보가 누락된 메뉴를 위한 기본 폴백 팔레트
const FALLBACK_PALETTE = [
  "linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)", // Blue
  "linear-gradient(135deg, #10B981 0%, #047857 100%)", // Emerald
  "linear-gradient(135deg, #F59E0B 0%, #D97706 100%)", // Amber
  "linear-gradient(135deg, #EC4899 0%, #BE185D 100%)", // Pink
  "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)", // Purple
  "linear-gradient(135deg, #06B6D4 0%, #0E7490 100%)", // Cyan
  "linear-gradient(135deg, #F97316 0%, #C2410C 100%)", // Orange
];

/**
 * 메뉴 아이콘의 컬러 반환 (메뉴 객체의 color 속성 사용)
 */
function getMenuIconBg(menu, index) {
  return menu.color || FALLBACK_PALETTE[index % FALLBACK_PALETTE.length];
}

/**
 * 메뉴 목록 조회 (userStore 또는 목업 API 연동)
 */
async function loadMenus() {
  isLoading.value = true;
  try {
    const list = await userStore.fetchUserMenu(userStore.userId);
    menus.value = list || [];
  } catch (error) {
    console.error("[ScmHome] 메뉴 로드 실패:", error);
  } finally {
    isLoading.value = false;
  }
}

// 홈 화면에서는 기본적으로 Home(/scm/home)을 제외한 업무 메뉴를 표시
// (만약 Home도 포함하고 싶으시다면 filter 조건을 제거하고 return menus.value; 로 변경하시면 됩니다)
const displayMenus = computed(() => {
  const filtered = menus.value.filter((m) => m.path !== "/scm/home");
  return filtered.length > 0 ? filtered : menus.value;
});

onMounted(() => {
  // 기존에 저장된 메뉴가 있으면 선반영
  if (userStore.myMenu && userStore.myMenu.length > 0) {
    menus.value = [...userStore.myMenu];
  }
  loadMenus();
});

// 사용자 아이디 변경 감지 시 메뉴 재조회
watch(
  () => userStore.userId,
  (newId) => {
    if (newId) {
      loadMenus();
    }
  }
);

/**
 * 메뉴 클릭 시 탭 추가 및 해당 경로로 라우팅
 */
function onMenuClick(menu) {
  tabStore.addTab(menu);
  router.push(menu.path);
}
</script>


